import React, { useState, useMemo, useEffect, useRef } from 'react'
import {
  Drawer,
  Checkbox,
  Button,
  Tag,
  Input,
  Tour,
  ConfigProvider,
} from 'antd'
import {
  DownloadOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  SyncOutlined,
  UndoOutlined,
} from '@ant-design/icons'
import './style.scss'
import invoiceApi from 'api/invoiceApi'
import ToastHelper from 'general/helpers/ToastHelper'
import JSZip from 'jszip'
import { saveAs } from 'file-saver'
import viVN from 'antd/locale/vi_VN'
import { ButtonDownload } from 'features/TaxDeduction/styled'

const CustomDrawer = ({
  drawerDowloadFie,
  setDrawerDowloadFie,
  title,
  width,
  files = [],
  clearDrawerDowloadFie,
}) => {
  const [selected, setSelected] = useState([])
  const [visibleCount, setVisibleCount] = useState(50)
  const [search, setSearch] = useState('')
  const [downloadingType, setDownloadingType] = useState(null)

  const [showSync, setShowSync] = useState(false)

  const [currentStep, setCurrentStep] = useState(0)
  const [openTour, setOpenTour] = useState(false)

  const refDownloadAll = useRef(null)
  const refPdf = useRef(null)
  const refXml = useRef(null)
  const [disableAllButton, setDisableAllButton] = useState(false)

  const lastChangeRef = useRef(Date.now())

  const steps = [
    {
      title: 'Tải toàn bộ file',
      description: 'Tải tất cả file PDF và XML thành file zip.',
      target: () => refDownloadAll.current,
    },
    {
      title: 'Tải PDF',
      description: 'Tải toàn bộ file PDF.',
      target: () => refPdf.current,
    },
    {
      title: 'Tải XML',
      description: 'Tải toàn bộ file XML.',
      target: () => refXml.current,
    },
  ]

  const handleClose = () => {
    setDrawerDowloadFie({
      ...drawerDowloadFie,
      show: false,
    })
  }

  const isSuccess = (item) => item.status === 'success'

  // =========================
  // SEARCH
  // =========================
  const filteredFiles = useMemo(() => {
    if (!search.trim()) return files

    const keyword = search.toLowerCase()

    return files.filter((item) => {
      const pdfName = item?.pdf?.nameFile?.toLowerCase() || ''
      const xmlName = item?.xml?.nameFile?.toLowerCase() || ''

      return pdfName.includes(keyword) || xmlName.includes(keyword)
    })
  }, [files, search])

  // =========================
  // STATUS COUNT
  // =========================
  const countStatus = useMemo(() => {
    const result = {
      success: 0,
      error: 0,
      processing: 0,
      waiting: 0,
    }

    filteredFiles.forEach((item) => {
      switch (item.status) {
        case 'success':
          result.success++
          break
        case 'error':
          result.error++
          break
        case 'processing':
          result.processing++
          break
        default:
          result.waiting++
      }
    })

    return result
  }, [filteredFiles])

  // =========================
  // FIX: PHẢI ĐẶT TRƯỚC USEEFFECT
  // =========================
  const isAllSuccessDone = useMemo(() => {
    if (!filteredFiles.length) return false
    return filteredFiles.every((item) => item.status === 'success')
  }, [filteredFiles])

  // =========================
  // TOUR
  // =========================
  useEffect(() => {
    if (!drawerDowloadFie?.show) return
    if (!isAllSuccessDone) return

    const isShown = localStorage.getItem('tour_download_drawer')
    if (isShown) return

    const timer = setTimeout(() => {
      setOpenTour(true)
    }, 500)

    return () => clearTimeout(timer)
  }, [drawerDowloadFie?.show, isAllSuccessDone])

  useEffect(() => {
    if (!isAllSuccessDone) {
      setOpenTour(false)
    }
  }, [isAllSuccessDone])

  // =========================
  // SYNC EFFECT
  // =========================
  useEffect(() => {
    lastChangeRef.current = Date.now()
    setShowSync(false)
  }, [countStatus.success, countStatus.error, countStatus.processing])

  useEffect(() => {
    const interval = setInterval(() => {
      const diff = Date.now() - lastChangeRef.current

      if (diff >= 1000) {
        setShowSync(true)
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  // =========================
  // KEYS
  // =========================
  const successKeys = useMemo(() => {
    const arr = []

    filteredFiles.forEach((item) => {
      if (isSuccess(item)) {
        if (item.pdf) {
          arr.push(`${item.invoiceId}_pdf`)
        }

        if (item.xml) {
          arr.push(`${item.invoiceId}_xml`)
        }
      }
    })

    return arr
  }, [filteredFiles])

  const pdfKeys = useMemo(() => {
    return filteredFiles
      .filter((item) => isSuccess(item) && item.pdf)
      .map((item) => `${item.invoiceId}_pdf`)
  }, [filteredFiles])

  const xmlKeys = useMemo(() => {
    return filteredFiles
      .filter((item) => isSuccess(item) && item.xml)
      .map((item) => `${item.invoiceId}_xml`)
  }, [filteredFiles])

  // =========================
  // SELECT
  // =========================
  const toggleSelect = (file, type) => {
    if (!isSuccess(file)) return

    const key = `${file.invoiceId}_${type}`

    const url = `${process.env.REACT_APP_BASE_URL}${file?.[type]?.fileURL}`

    const nameFile = type === 'pdf' ? file?.pdf?.nameFile : file?.xml?.nameFile

    setSelected((prev) => {
      const exists = prev.find((i) => i.key === key)

      if (exists) {
        return prev.filter((i) => i.key !== key)
      }

      return [
        ...prev,
        {
          key,
          url,
          nameFile,
        },
      ]
    })
  }

  const handleCheckAll = (e) => {
    if (e.target.checked) {
      const all = filteredFiles.flatMap((item) => {
        const arr = []

        if (item.pdf && isSuccess(item)) {
          arr.push({
            key: `${item.invoiceId}_pdf`,
            url: `${process.env.REACT_APP_BASE_URL}${item.pdf.fileURL}`,
            nameFile: item?.pdf?.nameFile,
          })
        }

        if (item.xml && isSuccess(item)) {
          arr.push({
            key: `${item.invoiceId}_xml`,
            url: `${process.env.REACT_APP_BASE_URL}${item.xml.fileURL}`,
            nameFile: item?.xml?.nameFile,
          })
        }

        return arr
      })

      setSelected(all)
    } else {
      setSelected([])
    }
  }

  const handleCheckType = (type, checked) => {
    const keys = type === 'pdf' ? pdfKeys : xmlKeys

    const items = filteredFiles.flatMap((item) => {
      if (!isSuccess(item)) return []

      if (type === 'pdf' && item.pdf) {
        return [
          {
            key: `${item.invoiceId}_pdf`,
            url: `${process.env.REACT_APP_BASE_URL}${item.pdf.fileURL}`,
            nameFile: item?.pdf?.nameFile,
          },
        ]
      }

      if (type === 'xml' && item.xml) {
        return [
          {
            key: `${item.invoiceId}_xml`,
            url: `${process.env.REACT_APP_BASE_URL}${item.xml.fileURL}`,
            nameFile: item?.xml?.nameFile,
          },
        ]
      }

      return []
    })

    if (checked) {
      setSelected((prev) => {
        const map = new Map(prev.map((i) => [i.key, i]))

        items.forEach((i) => map.set(i.key, i))

        return Array.from(map.values())
      })
    } else {
      setSelected((prev) => prev.filter((i) => !keys.includes(i.key)))
    }
  }

  const downloadFile = async (url, fileName) => {
    try {
      const res = await fetch(url)

      if (!res.ok) {
        throw new Error('Download failed')
      }

      const blob = await res.blob()

      const blobUrl = window.URL.createObjectURL(blob)

      const link = document.createElement('a')

      link.href = blobUrl
      link.download = fileName || 'download'

      document.body.appendChild(link)

      link.click()

      link.remove()

      window.URL.revokeObjectURL(blobUrl)
    } catch (err) {
      console.error(err)
    }
  }

  const buildDownloadItems = (type = 'all') => {
    return filteredFiles.flatMap((item) => {
      if (!isSuccess(item)) return []

      const arr = []

      if ((type === 'all' || type === 'pdf') && item.pdf) {
        arr.push({
          key: `${item.invoiceId}_pdf`,
          url: `${process.env.REACT_APP_BASE_URL}${item.pdf.fileURL}`,
          nameFile: item?.pdf?.nameFile,
        })
      }

      if ((type === 'all' || type === 'xml') && item.xml) {
        arr.push({
          key: `${item.invoiceId}_xml`,
          url: `${process.env.REACT_APP_BASE_URL}${item.xml.fileURL}`,
          nameFile: item?.xml?.nameFile,
        })
      }

      return arr
    })
  }

  const handleDownloadSelected = async (customSelected, type = 'selected') => {
    const list = customSelected || selected

    if (!list.length) return

    setDownloadingType(type)

    try {
      const zip = new JSZip()

      await Promise.all(
        list.map(async (item) => {
          const res = await fetch(item.url)

          if (!res.ok) return

          const blob = await res.blob()

          zip.file(item.nameFile, blob)
        }),
      )

      const zipBlob = await zip.generateAsync({
        type: 'blob',
      })

      saveAs(zipBlob, `invoices_${Date.now()}.zip`)
    } catch (err) {
      console.error(err)

      ToastHelper.showError('Lỗi tạo file zip')
    } finally {
      setDownloadingType(null)
      setDisableAllButton(false)
    }
  }

  const downloadFileRetry = async (invoiceIds) => {
    const res = await invoiceApi.downloadFileQueue({
      invoiceIds,
    })

    if (res === true) {
      ToastHelper.showSuccess('Đã làm mới file hóa đơn')

      return res
    }

    ToastHelper.showError('Lỗi tải file hóa đơn')
  }

  const renderStatus = (status) => {
    switch (status) {
      case 'success':
        return (
          <Tag color="green" style={{ height: 22 }}>
            Thành công
          </Tag>
        )

      case 'error':
        return (
          <Tag color="red" style={{ height: 22 }}>
            Lỗi
          </Tag>
        )

      case 'processing':
        return (
          <Tag color="orange" style={{ height: 22 }}>
            Đang xử lý
          </Tag>
        )

      default:
        return <Tag style={{ height: 22 }}>Chờ xử lý</Tag>
    }
  }

  const getDownloadButtonStyle = (color, lightColor, disabled) => ({
    background: disabled ? lightColor : color,
    border: `1px solid ${color}`,
    color: disabled ? color : '#fff',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  })

  const visibleFiles = useMemo(() => {
    return filteredFiles.slice(0, visibleCount)
  }, [filteredFiles, visibleCount])

  return (
    <ConfigProvider locale={viVN}>
      <Drawer
        title={title}
        placement="right"
        onClose={handleClose}
        open={drawerDowloadFie?.show}
        width={width}
      >
        <Tour
          open={openTour}
          current={currentStep}
          onChange={(value) => setCurrentStep(value)}
          onClose={() => {
            setOpenTour(false)
            localStorage.setItem('tour_download_drawer', '1')
          }}
          steps={[
            {
              title: 'Tải toàn bộ file',
              description: 'Tải tất cả file PDF và XML thành file zip.',
              target: () => refDownloadAll.current,
            },
            {
              title: 'Tải PDF',
              description: 'Tải toàn bộ file PDF.',
              target: () => refPdf.current,
            },
            {
              title: 'Tải XML',
              description: 'Tải toàn bộ file XML.',
              target: () => refXml.current,
            },
          ]}
        />

        <Tour
          open={openTour}
          current={currentStep}
          onChange={(value) => {
            setCurrentStep(value)
          }}
          onClose={() => {
            setOpenTour(false)

            localStorage.setItem('tour_download_drawer', '1')
          }}
          steps={steps}
          indicatorsRender={(current, total) => (
            <span>
              Bước {current + 1}/{total}
            </span>
          )}
        />

        <div className="dd-wrapper">
          <Input
            placeholder="Tìm file..."
            allowClear
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <div className="dd-header">
            <div className="dd-header-left">
              <Checkbox
                onChange={handleCheckAll}
                checked={
                  selected.length > 0 && selected.length === successKeys.length
                }
              >
                <span
                  style={{
                    width: 40,
                    display: 'inline-flex',
                    alignItems: 'center',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Tất cả
                </span>
              </Checkbox>

              <Checkbox
                onChange={(e) => handleCheckType('pdf', e.target.checked)}
                checked={
                  pdfKeys.length > 0 &&
                  pdfKeys.every((k) => selected.some((s) => s.key === k))
                }
              >
                PDF
              </Checkbox>

              <Checkbox
                onChange={(e) => handleCheckType('xml', e.target.checked)}
                checked={
                  xmlKeys.length > 0 &&
                  xmlKeys.every((k) => selected.some((s) => s.key === k))
                }
              >
                XML
              </Checkbox>
            </div>

            <div
              style={{
                display: 'flex',
                gap: 8,
                flexWrap: 'wrap',
                justifyContent: 'flex-end',
              }}
            >
              <ButtonDownload
                ref={refDownloadAll}
                size="small"
                icon={<DownloadOutlined />}
                disabled={
                  !successKeys.length ||
                  downloadingType === 'all' ||
                  filteredFiles.length != countStatus.success ||
                  disableAllButton
                }
                loading={downloadingType === 'all'}
                onClick={() => {
                  setDisableAllButton(true)
                  handleDownloadSelected(buildDownloadItems('all'), 'all')
                }}
                style={{
                  ...getDownloadButtonStyle(
                    '#1677ff',
                    '#e6f4ff',
                    !successKeys.length || downloadingType === 'all',
                  ),

                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4,
                  lineHeight: 1,
                }}
              >
                Tải tất cả
              </ButtonDownload>

              <ButtonDownload
                ref={refPdf}
                size="small"
                icon={<FilePdfOutlined />}
                disabled={
                  !pdfKeys.length ||
                  downloadingType === 'pdf' ||
                  filteredFiles.length != countStatus.success ||
                  disableAllButton
                }
                loading={downloadingType === 'pdf'}
                onClick={() => {
                  setDisableAllButton(true)
                  handleDownloadSelected(buildDownloadItems('pdf'), 'pdf')
                }}
                style={{
                  ...getDownloadButtonStyle(
                    '#ff4d4f',
                    '#fff1f0',
                    !pdfKeys.length || downloadingType === 'pdf',
                  ),

                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4,
                  lineHeight: 1,
                }}
              >
                Tải PDF
              </ButtonDownload>

              <ButtonDownload
                ref={refXml}
                size="small"
                icon={<FileTextOutlined />}
                disabled={
                  !xmlKeys.length ||
                  downloadingType === 'xml' ||
                  filteredFiles.length != countStatus.success ||
                  disableAllButton
                }
                loading={downloadingType === 'xml'}
                onClick={() => {
                  setDisableAllButton(true)
                  handleDownloadSelected(buildDownloadItems('xml'), 'xml')
                }}
                style={{
                  ...getDownloadButtonStyle(
                    '#13c2c2',
                    '#e6fffb',
                    !xmlKeys.length || downloadingType === 'xml',
                  ),

                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4,
                  lineHeight: 1,
                }}
              >
                Tải XML
              </ButtonDownload>

              <Button
                size="small"
                disabled={disableAllButton}
                onClick={() => {
                  clearDrawerDowloadFie()
                  setSelected([])
                }}
              >
                Xóa toàn bộ
              </Button>

              <Button
                style={{ minWidth: 120 }}
                size="small"
                type="primary"
                loading={downloadingType === 'selected'}
                disabled={
                  !selected.length ||
                  downloadingType === 'selected' ||
                  disableAllButton
                }
                onClick={() => handleDownloadSelected(selected, 'selected')}
              >
                {downloadingType === 'selected'
                  ? `Đang tải ${selected.length} file`
                  : `Tải ${selected.length} file`}
              </Button>
            </div>
          </div>

          <div className="dd-status">
            <Tag color="blue">Tổng ({filteredFiles.length})</Tag>

            <Tag color="green">Thành công ({countStatus.success})</Tag>

            <Tag color="red">Lỗi ({countStatus.error})</Tag>

            <Tag color="orange">Đang xử lý ({countStatus.processing})</Tag>

            <Tag>
              {showSync && countStatus.waiting > 0 && (
                <SyncOutlined
                  spin
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: 10,
                    transform: 'translateY(-50%)',
                  }}
                />
              )}
              Chờ ({countStatus.waiting})
            </Tag>
          </div>

          {visibleFiles.map((item) => (
            <div
              key={item.invoiceId}
              className={`dd-item ${!isSuccess(item) ? 'disabled' : ''}`}
            >
              <div className="dd-item-header">
                <span>
                  {item.serial} - {item.no}
                </span>

                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                  }}
                >
                  {renderStatus(item.status)}

                  <Button
                    icon={<UndoOutlined />}
                    onClick={() => downloadFileRetry([item.invoiceId])}
                  />
                </div>
              </div>

              <div className="dd-files">
                <div
                  className="dd-file pdf"
                  onClick={() =>
                    downloadFile(
                      `${process.env.REACT_APP_BASE_URL}${item?.pdf?.fileURL}`,
                      item?.pdf?.nameFile,
                    )
                  }
                >
                  <Checkbox
                    disabled={!isSuccess(item)}
                    checked={selected.some(
                      (i) => i.key === `${item.invoiceId}_pdf`,
                    )}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggleSelect(item, 'pdf')}
                  />

                  <FilePdfOutlined className="icon" />

                  <span>{item?.pdf?.nameFile}</span>
                </div>

                <div
                  className="dd-file xml"
                  onClick={() =>
                    downloadFile(
                      `${process.env.REACT_APP_BASE_URL}${item?.xml?.fileURL}`,
                      item?.xml?.nameFile,
                    )
                  }
                >
                  <Checkbox
                    disabled={!isSuccess(item)}
                    checked={selected.some(
                      (i) => i.key === `${item.invoiceId}_xml`,
                    )}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggleSelect(item, 'xml')}
                  />

                  <FileTextOutlined className="icon" />

                  <span>{item?.xml?.nameFile}</span>
                </div>
              </div>
            </div>
          ))}

          {visibleCount < filteredFiles.length && (
            <Button
              block
              size="small"
              onClick={() => setVisibleCount((prev) => prev + 50)}
            >
              Xem thêm ({filteredFiles.length - visibleCount})
            </Button>
          )}

          <Button
            type="primary"
            block
            loading={downloadingType === 'selected'}
            disabled={!selected.length || downloadingType === 'selected'}
            onClick={() => handleDownloadSelected(selected, 'selected')}
          >
            {downloadingType === 'selected'
              ? `Đang tải ${selected.length} file`
              : `Tải ${selected.length} file`}
          </Button>
        </div>
      </Drawer>
    </ConfigProvider>
  )
}

export default CustomDrawer
